// import { createRouter, createWebHashHistory, RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { nextTick } from "vue";
import { useAuthStore } from "@/store/auth";
import { queryClient } from "@/queryClient";
import {
  getSideNavigationQueryKey,
  resolveSideNavigationQuery,
} from "@/composables/useSideNavigationQuery";
import {
  canAccessRoute,
  getDefaultRouteForAuth,
  getClientFirstEntryRoute,
  isBlockedInternalUser,
  isClientRestrictedRoute,
} from "@/utils/authRedirect";

import Login from "@/views/Login.vue";
import DashboardView from "@/views/DashboardView.vue";
import ReportsView from "@/views/ReportsView.vue";
import ManageClientSidenav from "@/views/admin/ManageClientSidenav.vue";
import RenewalManagementView from "@/views/RenewalManagementView.vue";
import { useLoaderStore } from "../store/loader"; // import loader store

const routes: Array<RouteRecordRaw> = [
  {
    path: "/client/first-entry",
    name: "ClientFirstEntry",
    component: () => import("@/views/client/onboarding/ClientFirstEntryView.vue"),
    meta: { requiresAuth: true, allowedUserTypes: ["client"], title: "Digital Marketing Suite | Getting Started" },
  },
  {
    path: "/client/initial-plan",
    name: "ClientInitialPlan",
    component: () => import("@/views/client/onboarding/ClientInitialPlanView.vue"),
    meta: { requiresAuth: true, allowedUserTypes: ["client"], title: "Digital Marketing Suite | Initial DMP Plan" },
  },
  {
    path: "/",
    name: "Login",
    component: Login,
    meta: { title: "Digital Marketing Suite | Login" },
  },
  {
    path: "/privacy-policy",
    name: "PrivacyPolicy",
    component: () =>
      import(
        /* webpackChunkName: "legal-document" */ "@/views/LegalDocumentView.vue"
      ),
    props: { documentKey: "privacy_policy" },
    meta: {
      isPublic: true,
      title: "Digital Marketing Suite | Privacy Policy",
    },
  },
  {
    path: "/terms-and-conditions",
    name: "TermsAndConditions",
    component: () =>
      import(
        /* webpackChunkName: "legal-document" */ "@/views/LegalDocumentView.vue"
      ),
    props: { documentKey: "terms_and_conditions" },
    meta: {
      isPublic: true,
      title: "Digital Marketing Suite | Terms & Conditions",
    },
  },

  {
    path: "/overview",
    name: "Overview",
    component: DashboardView,
    meta: {
      requiresAuth: true,
      keepAlive: true,
      title: "Digital Marketing Suite | Overview Dashboard",
    },
  },

  {
    path: "/inbox",
    name: "Inbox",
    component: () =>
      import(/* webpackChunkName: "inbox" */ "@/modules/inbox/App.vue"),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Inbox",
      disablePageScroll: true,
    },
  },

  {
    path: "/reports",
    name: "Reports",
    component: ReportsView,
    meta: {
      requiresAuth: true,
      keepAlive: true,
      title: "Digital Marketing Suite | Reports",
    },
  },
  {
    path: "/reports/dashboard-preview",
    name: "DashboardReportPreview",
    component: () =>
      import(
        /* webpackChunkName: "dashboard-report-preview" */ "@/views/reports/DashboardReportPreviewView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Dashboard Report Preview",
    },
  },
  {
    path: "/reports-v2",
    name: "ReportsV2Dashboard",
    component: () =>
      import(
        /* webpackChunkName: "reports-v2-dashboard" */ "@/views/reports-v2/ReportsV2DashboardView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Reports v2 Dashboard",
      keepAlive: false,
      stableRouteKey: "reports-v2-dashboard",
    },
  },
  {
    path: "/reports-v2/seo-reports",
    name: "SeoReportsCalendar",
    component: () =>
      import(
        /* webpackChunkName: "seo-reports-calendar" */ "@/views/reports-v2/SeoReportsCalendarView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | SEO Reports Calendar",
      keepAlive: false,
      stableRouteKey: "seo-reports-calendar",
    },
  },

  {
    path: "/activity",
    name: "Activity",
    component: () =>
      import(
        /* webpackChunkName: "activity-view" */ "@/views/ActivityView.vue"
      ),
    meta: {
      requiresAuth: true,
      keepAlive: true,
      title: "Digital Marketing Suite | Activity",
    },
  },

  // USERS section
  {
    path: "/users",
    name: "Users",
    component: () =>
      import(/* webpackChunkName: "internal-users" */ "@/views/UsersView.vue"),
    meta: {
      requiresAuth: true,
      keepAlive: true,
      title: "Digital Marketing Suite | Users",
    },
  },
  {
    path: "/clients",
    name: "Clients",
    component: () =>
      import(
        /* webpackChunkName: "client-management" */ "@/views/ClientManagementView.vue"
      ),
    meta: {
      requiresAuth: true,
      keepAlive: true,
      title: "Digital Marketing Suite | Clients",
    },
  },
  {
    path: "/dmp-accounts",
    name: "DmpAccounts",
    component: () =>
      import(
        /* webpackChunkName: "client-management" */ "@/views/ClientManagementView.vue"
      ),
    meta: {
      requiresAuth: true,
      keepAlive: true,
      title: "Digital Marketing Suite | DMP Accounts",
    },
  },
  {
    path: "/renewal-tracking",
    name: "RenewalTracking",
    component: () =>
      import(
        /* webpackChunkName: "renewal-tracking" */ "@/views/RenewalTrackingView.vue"
      ),
    meta: {
      requiresAuth: true,
      keepAlive: true,
      title: "Digital Marketing Suite | Renewal Tracking",
    },
  },
  {
    path: "/clients/:id",
    alias: "/users/clients/:id",
    name: "ClientProfile",
    component: () =>
      import(
        /* webpackChunkName: "client-profile" */ "@/views/users/ClientProfileView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Client Profile",
    },
  },
  {
    path: "/renewals",
    name: "RenewalManagement",
    component: RenewalManagementView,
    meta: {
      requiresAuth: true,
      allowedUserTypes: ["internal_user"],
      title: "Digital Marketing Suite | Renewal",
    },
  },
  {
    path: "/email-templates/:templateSlug(balance-collection|renewal|offboarding-email|end-of-contract)",
    name: "EmailTemplates",
    component: () =>
      import(
        /* webpackChunkName: "email-templates" */ "@/views/EmailTemplatesView.vue"
      ),
    props: true,
    meta: {
      requiresAuth: true,
      allowedUserTypes: ["internal_user"],
      title: "Digital Marketing Suite | Email Templates",
    },
  },

  // Profile Management Routes
  {
    path: "/profile",
    name: "Profile",
    component: () =>
      import(/* webpackChunkName: "profile" */ "@/views/ProfileView.vue"),
    meta: { requiresAuth: true, title: "Digital Marketing Suite | Profile" },
  },
  {
    path: "/client-profile",
    name: "ClientSelfProfile",
    component: () =>
      import(
        /* webpackChunkName: "client-self-profile" */ "@/views/client/ClientSelfProfileView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | My Profile",
    },
  },
  {
    path: "/onboarding",
    name: "Onboarding",
    component: () =>
      import(
        /* webpackChunkName: "onboarding-wrapper" */ "@/views/onboarding/OnboardingRouterView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Onboarding",
    },
  },
  {
    path: "/client/onboarding",
    redirect: "/onboarding",
  },
  {
    path: "/client/dashboard-v2",
    name: "ClientDashboardV2",
    component: () =>
      import(
        /* webpackChunkName: "client-dashboard-v2" */ "@/views/client/ClientDashboardV2.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Client Dashboard V2",
    },
  },
  {
    path: "/clients/microapp",
    name: "MicroappAccounts",
    component: () =>
      import(
        /* webpackChunkName: "microapp-accounts" */ "@/views/MicroappManagementView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Microapp accounts",
    },
  },
  {
    path: "/onboarding/microapp",
    name: "MicroappOnboarding",
    component: () =>
      import(
        /* webpackChunkName: "microapp-onboarding" */ "@/views/onboarding/MicroappOnboardingView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Microapp Onboarding",
    },
  },
  {
    path: "/updates",
    name: "Updates",
    component: () =>
      import(/* webpackChunkName: "updates" */ "@/views/UpdatesView.vue"),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Updates",
    },
  },

  {
    path: "/projects",
    name: "Projects",
    component: () =>
      import(/* webpackChunkName: "projects" */ "@/views/ProjectsView.vue"),
    meta: { requiresAuth: true, title: "Digital Marketing Suite | Projects" },
  },

  {
    path: "/subscription",
    name: "Subscription",
    component: () =>
      import(
        /* webpackChunkName: "subscription" */ "@/views/SubscriptionView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Subscription",
    },
  },

  {
    path: "/security",
    name: "Security",
    component: () =>
      import(/* webpackChunkName: "security" */ "@/views/SecurityView.vue"),
    meta: { requiresAuth: true, title: "Digital Marketing Suite | Security" },
  },

  {
    path: "/account-settings",
    name: "AccountSettings",
    component: () =>
      import(
        /* webpackChunkName: "account-settings" */ "@/views/AccountSettingsView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Account Settings",
    },
  },

  {
    path: "/apps/task-dashboard",
    name: "TaskDashboard",
    component: () =>
      import(/* webpackChunkName: "task-dashboard" */ "@/modules/task/App.vue"),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Task Overview",
    },
  },

  {
    path: "/apps/tasks/:status",
    name: "TaskTableView",
    component: () => import("@/modules/task/views/TaskTableView.vue"),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Task Status",
    },
    props: true,
  },

  // Lazy-loaded Apps Section

  {
    path: "/apps/wp-dashboard",
    name: "WpDashboard",
    component: () =>
      import(
        /* webpackChunkName: "wp-dashboard" */ "@/modules/wp-dashboard/App.vue"
      ),
    meta: { requiresAuth: true, title: "Digital Marketing Suite | WordPress" },
  },

  {
    path: "/apps/forms-datapoint",
    name: "FormsDataPoint",
    component: () =>
      import(
        /* webpackChunkName: "forms-datapoint" */ "@/modules/forms-datapoint/App.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Forms Datapoint",
    },
  },

  {
    path: "/apps/request-form",
    name: "RequestForm",
    component: () =>
      import(
        /* webpackChunkName: "request-form" */ "@/modules/request-form/App.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Request Form",
    },
  },

  {
    path: "/apps/onboarding",
    redirect: "/onboarding",
  },

  {
    path: "/onboarding/:id",
    name: "OnboardingDetails",
    component: () =>
      import(
        /* webpackChunkName: "onboarding-details" */ "@/modules/onboarding/views/OnboardingDetailsView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Onboarding Details",
    },
  },

  {
    path: "/apps/onboarding/:id",
    redirect: (to) => ({
      path: `/onboarding/${to.params.id}`,
      query: to.query,
      hash: to.hash,
    }),
  },

  // Communication Section

  {
    path: "/communication/chat",
    name: "ChatTool",
    component: () =>
      import(/* webpackChunkName: "chat-tool" */ "@/modules/chat/App.vue"),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Chat",
      disablePageScroll: true,
    },
  },

  {
    path: "/leads",
    name: "Leads",
    component: () =>
      import(/* webpackChunkName: "leads-view" */ "@/views/LeadsView.vue"),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Leads",
    },
  },
  {
    path: "/leads/account/:account",
    name: "LeadsAccount",
    component: () =>
      import(/* webpackChunkName: "leads-view" */ "@/views/LeadsView.vue"),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Leads",
    },
  },
  {
    path: "/leads/:id",
    name: "LeadDetails",
    component: () =>
      import(
        /* webpackChunkName: "lead-details" */ "@/views/LeadDetailsView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Lead Details",
    },
  },

  // Operations Section

  {
    path: "/operations/task",
    name: "TaskManagement",
    component: () =>
      import(
        /* webpackChunkName: "task-management" */ "@/modules/task/App.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Task Overview",
    },
  },

  {
    path: "/operations/job-scheduling",
    name: "JobScheduling",
    component: () =>
      import(
        /* webpackChunkName: "job-scheduling" */ "@/modules/job-scheduling/App.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Job Scheduling",
    },
  },

  // Placeholder Route for Add Section

  {
    path: "/addSection",
    name: "Add Section",
    component: () =>
      import(
        /* webpackChunkName: "users-placeholder" */ "@/views/AddSection.vue"
      ),
  },

  // Placeholder Route for New Application

  {
    path: "/new-application",
    name: "New Application",
    component: () =>
      import(
        /* webpackChunkName: "new-application-placeholder" */ "@/views/NewApplication.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | New Application",
    },
  },

  {
    path: "/admin/manage-client-sidenav",
    name: "Manage Client Sidenav",
    component: ManageClientSidenav,
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Admin Dashboard",
    }, // optional
  },

  {
    path: "/admin/package-services",
    name: "PackageServicesManagement",
    component: () =>
      import(
        /* webpackChunkName: "package-services-management" */ "@/views/admin/PackageServicesManagement.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Package & Services Management",
    },
  },

  {
    path: "/admin/package-services/:id",
    name: "PackageDetail",
    component: () =>
      import(
        /* webpackChunkName: "package-detail" */ "@/views/admin/PackageDetailView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Package Details",
    },
  },

  {
    path: "/:category(apps|communication|operations)/:slug",
    name: "CustomAppBySectionAndSlug",
    component: () => import("@/views/CustomAppLoader.vue"),
    props: true,
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite ",
      embedded: true, // iframe pool; CustomAppLoader not mounted when orchestrator is active
    },
  },

  /**
   * slug-based custom app route
   * e.g. #/apps/custom/web-diag
   */
  {
    path: "/apps/custom/:slug",
    name: "CustomAppBySlug",
    component: () => import("@/views/CustomAppLoader.vue"),
    props: true,
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Custom Module",
      embedded: true,
    },
  },
  {
    path: "/:slug([A-Za-z0-9-]+)/:appPath(.*)*",
    name: "CustomAppNestedSlug",
    component: () => import("@/views/CustomAppLoader.vue"),
    props: true,
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Custom Module",
      embedded: true,
    },
  },

  {
    path: "/:slug([A-Za-z0-9-]+)",
    name: "CustomAppDirectSlug",
    component: () => import("@/views/CustomAppLoader.vue"),
    props: true,
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Custom Module",
      embedded: true,
    },
  },

  /**
   * BACK-COMPAT: id-based route still supported
   * e.g. #/apps/custom/id/25
   * CustomAppLoader will accept either param and fetch accordingly
   */
  {
    path: "/apps/custom/id/:id",
    name: "CustomAppById",
    component: () => import("@/views/CustomAppLoader.vue"),
    props: true,
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Custom Module",
      embedded: true,
    },
  },

  // System Apps Management (Super Admin only)
  {
    path: "/settings/system-apps",
    name: "SystemAppsManagement",
    component: () =>
      import(
        /* webpackChunkName: "system-configuration" */ "@/views/SystemConfigurationView.vue"
      ),
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      title: "Digital Marketing Suite | System Apps Management",
    },
  },
  {
    path: "/settings/notifications",
    name: "NotificationManagement",
    component: () =>
      import(
        /* webpackChunkName: "notification-management" */ "@/views/NotificationManagementView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Notification Management",
    },
  },
  {
    path: "/settings/system",
    name: "SystemSettings",
    component: () =>
      import(
        /* webpackChunkName: "system-settings" */ "@/views/SystemSettingsView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | System Settings",
    },
  },
  {
    path: "/settings/email-recipients",
    name: "EmailNotificationRecipients",
    component: () =>
      import(
        /* webpackChunkName: "email-recipients" */ "@/views/admin/EmailNotificationRecipientsView.vue"
      ),
    meta: {
      requiresAuth: true,
      title: "Digital Marketing Suite | Email Notification Recipients",
    },
  },

  {
    path: "/settings/holidays",
    name: "ManageHolidays",
    component: () => import(/* webpackChunkName: "manage-holidays" */ "@/views/ManageHolidaysView.vue"),
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      title: "Digital Marketing Suite | Manage Holidays",
    },
  },

  // 404 Page
  {
    path: "/404",
    name: "NotFound",
    component: () =>
      import(/* webpackChunkName: "not-found" */ "@/views/NotFound.vue"),
    meta: { title: "Digital Marketing Suite | Page Not Found" },
  },

  { path: "/:pathMatch(.*)*", redirect: "/404" },
];

// Create Router Instance

const router = createRouter({
  // history: createWebHashHistory(process.env.BASE_URL),
  history: createWebHistory(process.env.BASE_URL), //Use this for New Production Server (Ngingx Config)
  routes,
});

function getDefaultRedirectPath(
  currentPath: string,
  auth: ReturnType<typeof useAuthStore>
): string {
  const defaultRoute = getDefaultRouteForAuth(auth);
  const fallbackRoute =
    auth.accountType === "client" ? "/client-profile" : "/profile";

  if (
    !defaultRoute ||
    defaultRoute === currentPath ||
    (auth.accountType === "client" && isClientRestrictedRoute(defaultRoute))
  ) {
    return fallbackRoute;
  }

  return defaultRoute;
}

// Add Navigation Guards

router.beforeEach(async (to, from, next) => {
  const loader = useLoaderStore();
  loader.start();
  const title = (to.meta.title as string) || "Default Title";
  document.title = title;

  const auth = useAuthStore();
  if (!auth.hydrated && !to.meta.isPublic) {
    await auth.restoreSession();
  }

  const firstEntryRoute = getClientFirstEntryRoute(auth);
  if (firstEntryRoute && !to.meta.isPublic && to.path !== firstEntryRoute) {
    return next(firstEntryRoute);
  }
  if (auth.accountType === "client" && !firstEntryRoute && to.path === "/client/first-entry") {
    return next(getDefaultRedirectPath(to.path, auth));
  }

  if (auth.isAuthenticated && !isBlockedInternalUser(auth) && !firstEntryRoute) {
    await queryClient.prefetchQuery({
      queryKey: getSideNavigationQueryKey(auth),
      queryFn: () => resolveSideNavigationQuery(auth),
    });
  }

  if (to.path === "/" && auth.isAuthenticated) {
    return next(getDefaultRedirectPath(to.path, auth));
  }
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return next("/");
  }
  if (
    auth.isAuthenticated &&
    to.meta.allowedUserTypes &&
    Array.isArray(to.meta.allowedUserTypes)
  ) {
    const isAllowed = to.meta.allowedUserTypes.some(
      (type: string) => type === auth.accountType || type === auth.userType
    );
    if (!isAllowed) {
      return next(getDefaultRedirectPath(to.path, auth));
    }
  }
  if (to.meta.requiresSuperAdmin && !auth.isSuperAdmin) {
    return next(getDefaultRedirectPath(to.path, auth));
  }
  if (auth.isAuthenticated && isBlockedInternalUser(auth)) {
    await auth.logout();
    return next("/");
  }
  if (auth.accountType === "client" && isClientRestrictedRoute(to.path)) {
    return next(getDefaultRedirectPath(to.path, auth));
  }
  if (auth.isAuthenticated && !canAccessRoute(to.path, auth)) {
    return next(getDefaultRedirectPath(to.path, auth));
  }
  if (to.path === "/new-application" && !auth.isSuperAdmin) {
    return next(getDefaultRedirectPath(to.path, auth));
  }
  if (to.path === "/activity" && !auth.isSuperAdmin) {
    return next(getDefaultRedirectPath(to.path, auth));
  }
  if (
    to.path === "/settings/notifications" &&
    !(
      auth.accountType === "internal_user" &&
      ["1", "2"].includes(String(auth.userLevel))
    )
  ) {
    return next(getDefaultRedirectPath(to.path, auth));
  }
  if (
    (to.path === "/settings/system" || to.path === "/settings/email-recipients") &&
    !auth.isSuperAdmin
  ) {
    return next(getDefaultRedirectPath(to.path, auth));
  }

  next();
});

router.afterEach(() => {
  const loader = useLoaderStore();
  loader.stop();
});

export default router;
